import { toTitleCase } from '@dispatch/shared';
import { Modal } from '@dispatch/web/ui';
import React from 'react';

import {
  AddAttributesContent,
  AddGroupAttributeContent,
  AddGroupContent,
  AddLicenseAndCertContent,
  AddOfficeContent,
  AddResourceRoleContent,
  AddTeamContent,
  DeleteContent,
} from './modalContent';
import {
  Company,
  CompanyAdminMeta,
  CompanyGroupAttributes,
} from '@dispatch/shared/types';
import { onCancel } from '@dispatch/web/features/company-admin';

export const AddEditModal = ({
  modal,
  setModal,
  handleSave,
  handleDelete,
  companyData,
  groupAttributes,
}: {
  modal: CompanyAdminMeta;
  setModal: React.Dispatch<CompanyAdminMeta>;
  handleSave: (
    body: unknown & { integrationGUID: string },
    key?: string
  ) => void;
  handleDelete: (type: string, integrationGUID: string) => void;
  companyData?: Company;
  groupAttributes?: CompanyGroupAttributes[];
}) => {
  const anyTrue = Object.values(modal)?.some(
    (value: { open: boolean }) => value.open === true
  );
  const key = Object.keys(modal)?.find(
    (key) => modal[key as keyof typeof modal].open === true
  );
  const nextAttributeId = React.useMemo(() => {
    let sortedData: number[] = [];
    if (companyData?.attributeDefinition) {
      sortedData = companyData.attributeDefinition.map((item) => item.id || 0);
    }
    const nextId = sortedData.length > 0 ? Math.max(...sortedData) : 0;

    return nextId + 1;
  }, [companyData?.attributeDefinition]);

  const nextRoleId = React.useMemo(() => {
    let sortedData: number[] = [];
    if (companyData?.role) {
      sortedData = companyData.role.map((item) => item.id || 0);
    }
    const nextId = sortedData.length > 0 ? Math.max(...sortedData) : 0;

    return nextId + 1;
  }, [companyData?.role]);

  const nextCertAndLicenseId = React.useMemo(() => {
    let sortedData: number[] = [];
    if (companyData?.certificationAndLicense) {
      sortedData = companyData.certificationAndLicense.map(
        (item) => item.id || 0
      );
    }
    const nextId = sortedData.length > 0 ? Math.max(...sortedData) : 0;

    return nextId + 1;
  }, [companyData?.certificationAndLicense]);

  const renderContent = () => {
    switch (key) {
      case 'addAttribute':
        return (
          <AddAttributesContent
            id={nextAttributeId}
            modalState={modal}
            handleSave={handleSave}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );
      case 'addGroupAttribute':
        return (
          <AddGroupAttributeContent
            id={nextAttributeId}
            modalState={modal}
            handleSave={handleSave}
            handleCancel={() => onCancel(key, modal, setModal)}
            groupAttributes={groupAttributes}
          />
        );
      case 'addResourceRole':
        return (
          <AddResourceRoleContent
            id={nextRoleId}
            modalState={modal}
            handleSave={handleSave}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );
      case 'addTeam':
        return (
          <AddTeamContent
            handleSave={handleSave}
            modalState={modal}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );

      case 'addGroup':
        return (
          <AddGroupContent
            handleSave={handleSave}
            modalState={modal}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );

      case 'addOffice':
        return (
          <AddOfficeContent
            modalState={modal}
            handleSave={handleSave}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );

      case 'addCertAndLicense':
        return (
          <AddLicenseAndCertContent
            id={nextCertAndLicenseId}
            modalState={modal}
            handleSave={handleSave}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );
      case 'delete':
        return (
          <DeleteContent
            modalState={modal}
            handleDelete={handleDelete}
            handleCancel={() => onCancel(key, modal, setModal)}
          />
        );
      default:
        return <>Form Not Found</>;
    }
  };

  const renderTitle = React.useMemo(() => {
    const foundTitle = Object.keys(modal).flatMap((key) => {
      if (modal[key as keyof typeof modal].open === true) {
        return key;
      }
      return [];
    });

    if (foundTitle.length > 0) {
      if (foundTitle[0] === 'addGroupAttribute') {
        return modal.addGroupAttribute?.data
          ? 'Edit Group attribute'
          : 'Create Group attribute';
      }

      const title = foundTitle[0].replace(/([a-z])([A-Z])/g, '$1 $2');
      return toTitleCase(title);
    }
    return ' ';
  }, [modal]);

  const renderModal = React.useMemo(() => {
    return (
      <Modal
        testId="assign-attributes-modal"
        open={anyTrue}
        handleClickAway={() => onCancel(key || '', modal, setModal)}
        title={renderTitle}
      >
        {renderContent()}
      </Modal>
    );
  }, [modal]);

  return <>{renderModal}</>;
};
